// GlossaryTerm — the ONE hover/tap definition surface for jargon (AC-UX-2,
// 05 §6 rule 1 "plain language first"). Definitions come exclusively from the
// shared glossary (packages/shared/src/glossary.js); do not fork per-page
// definitions — extend that list instead.
//
// Usage:
//   <GlossaryTerm term="LOE" />               → renders "LOE" with definition
//   <GlossaryTerm term="LOE">LOEs</GlossaryTerm> → custom display text
//   wrapGlossaryTerms('Track LOE intake')     → text nodes with known terms
//                                               wrapped in <GlossaryTerm>
import { Fragment, useId } from 'react';
import { glossary } from '@dha-nexus/shared';
import './glossaryTerm.css';

export function GlossaryTerm({ term, children }) {
  const tipId = useId();
  const entry = glossary.getGlossaryEntry(term);
  const label = children ?? term;
  // Unknown terms render as plain text — never a broken affordance.
  if (!entry) return <>{label}</>;
  return (
    <span className="glossary-term" data-testid={`glossary-term-${entry.term}`}>
      {/* tabIndex makes the definition keyboard-reachable (hover OR focus). */}
      <span className="glossary-term-label" tabIndex={0} aria-describedby={tipId}>
        {label}
      </span>
      <span role="tooltip" id={tipId} className="glossary-term-tip">
        {entry.expansion ? <strong>{entry.expansion}</strong> : null}
        <span>{entry.definition}</span>
      </span>
    </span>
  );
}

// Matches whole-word occurrences of AUTO-WRAP-SAFE glossary terms (longest
// first so e.g. "POA&M" wins over any shorter overlap). Terms flagged
// autoWrap:false (common English words) are excluded here so they are never
// wrapped into arbitrary body copy — explicit <GlossaryTerm> still resolves
// them via the full glossary. Built lazily once per session.
let termPattern = null;
function getTermPattern() {
  if (termPattern) return termPattern;
  const escaped = glossary.listAutoWrapTerms().map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  termPattern = new RegExp(`(?<![A-Za-z0-9])(${escaped.join('|')})(?![A-Za-z0-9])`, 'g');
  return termPattern;
}

/**
 * Wrap every known glossary term inside a plain string with <GlossaryTerm>.
 * Non-string input is returned untouched, so callers can pass through
 * whatever they were already rendering.
 */
export function wrapGlossaryTerms(text) {
  if (typeof text !== 'string' || !text) return text;
  const pattern = getTermPattern();
  pattern.lastIndex = 0;
  const parts = [];
  let cursor = 0;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) parts.push(text.slice(cursor, match.index));
    parts.push(<GlossaryTerm key={`${match[1]}-${match.index}`} term={match[1]} />);
    cursor = match.index + match[1].length;
  }
  if (!parts.length) return text;
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts.map((part, i) => <Fragment key={i}>{part}</Fragment>);
}
